import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Icon } from '../../../components/ui/Icon';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Skeleton } from '../../../components/ui/Skeleton';
import { MEmpty, MGroup, MPage, MRow, MSeg } from '../../../components/mobile/MobileKit';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { AdminHeader } from '../shared/AdminShell';
import { SectionMark } from '../../../components/ui/SectionMark';
import '../fatture/fatture.css';
import { AzioniTassa, NuovaTassa } from './AzioniTassa';
import { Fatto, Foglio, Testo } from '../shared/AdminMobile';
import {
  DOCUMENTO_LABEL, conArticolo, dataCorta, debitiBachecaQuery,
  type BachecaDebiti, type CartaDebito, type ColonnaBacheca, type ColonnaDebiti, type RataDebito,
} from './debitiApi';
import '../admin.css';
import './debiti.css';

/**
 * Debiti col fisco (`/admin/debiti`): la bacheca, idea di Lorenzo del 16/10
 * ("un mix tra Trello e Calendar: cosa è rateizzato e cosa no, le rate pagate
 * e le nuove").
 *
 * Tre colonne: da sistemare, a rate, chiusi. Le carte NON si trascinano: la
 * colonna la decidono i dati (le rate si spuntano dalla banca, i documenti
 * arrivano dalla PEC), così la bacheca non si stacca dalla realtà come il
 * vecchio Trello. Cliccando una carta si apre la scheda con tutte le rate.
 *
 * Computer e telefono hanno due impaginazioni diverse (regola di Lorenzo del
 * 3/9): sul computer le colonne affiancate, sul telefono una colonna alla volta.
 */
export function DebitiPage() {
  const isMobile = useIsMobile();
  const q = useQuery(debitiBachecaQuery);
  const [aperta, setAperta] = useUrlState<string>('carta', '');
  const carte = useMemo(() => q.data?.colonne.flatMap((c) => c.carte) ?? [], [q.data]);
  const scelta = carte.find((c) => c.id === aperta) ?? null;
  const oggi = q.data?.oggi ?? '';

  return isMobile
    ? <DebitiMobile q={q} oggi={oggi} scelta={scelta} onApri={setAperta} />
    : <DebitiDesktop q={q} oggi={oggi} scelta={scelta} onApri={setAperta} />;
}

type Q = UseQueryResult<BachecaDebiti>;
interface VistaProps { q: Q; oggi: string; scelta: CartaDebito | null; onApri: (id: string) => void }

// ════════════════════════════════════════════════════════════════════
// Computer
// ════════════════════════════════════════════════════════════════════

/**
 * Rifinita il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md: "la bacheca c'è già, rifinire").
 * La domanda: quanto dobbiamo al fisco e da cosa cominciare?
 * La forma resta la bacheca a tre colonne; in cima la risposta in una frase con "da dove
 * cominciare" (la prima carta da sistemare, quella con la scadenza più vicina). Tolti: il
 * sottotitolo lungo, il bottone del calendario (sta nel menu a sinistra), le descrizioni sotto i
 * titoli delle colonne (restano come suggerimento sul titolo). Nella scheda: la prossima rata non
 * si ripete sopra l'elenco delle rate, la scadenza dell'F24 non si ripete sotto lo stato, e una
 * riga dice in parole semplici cos'è il documento (cartella, avviso, piano, F24).
 */
function DebitiDesktop({ q, oggi, scelta, onApri }: VistaProps) {
  return (
    <div className="page adm-page ft-page deb-page">
      <header className="ft-hd">
        <div className="ft-hd-main">
          <SectionMark icon="landmark" className="ft-sez" />
          <div className="min-w-0">
            <h1 className="ft-title">Debiti col fisco</h1>
            <p className="ft-risposta">{q.data ? <Risposta b={q.data} onApri={onApri} /> : 'Leggo i debiti col fisco…'}</p>
          </div>
        </div>
        <div className="ft-hd-azioni"><NuovaTassa /></div>
      </header>

      {q.isLoading && !q.data ? (
        <div className="kanban deb-board">
          {[0, 1, 2].map((i) => (
            <div className="kanban-col" key={i}>
              <div className="kanban-col-hd"><Skeleton size="sm" w={140} /><Skeleton size="sm" w={24} /></div>
              <div className="kanban-col-body"><Skeleton size="block" h={96} /><Skeleton size="block" h={96} /></div>
            </div>
          ))}
        </div>
      ) : !q.data ? (
        <EmptyState icon="landmark" title="Debiti col fisco non disponibili" description="Non riesco a leggere i debiti in questo momento. Riprova tra poco." />
      ) : (
        <div className="kanban deb-board">
          {q.data.colonne.map((col) => <Colonna key={col.key} col={col} oggi={oggi} onApri={onApri} />)}
        </div>
      )}

      <Pannello carta={scelta} oggi={oggi} onClose={() => onApri('')} />
    </div>
  );
}

/** "Ci sono 12 debiti da sistemare e 2 piani a rate in corso. Da dove cominciare: …" */
function Risposta({ b, onApri }: { b: BachecaDebiti; onApri: (id: string) => void }) {
  const sistemare = b.colonne.find((c) => c.key === 'sistemare');
  const rate = b.colonne.find((c) => c.key === 'rate');
  const n = sistemare?.carte.length ?? 0;
  const m = rate?.carte.length ?? 0;
  const primo = sistemare?.carte[0];
  if (!n && !m) return <>Niente da sistemare col fisco e nessun piano a rate in corso.</>;
  return (
    <>
      {n > 0 ? <>Ci sono <strong>{n === 1 ? 'un debito da sistemare' : `${n} debiti da sistemare`}</strong></> : <>Nessun debito da sistemare</>}
      {m > 0 ? <> e {m === 1 ? 'un piano a rate in corso' : `${m} piani a rate in corso`}.</> : '.'}
      {primo && (
        <> Da dove cominciare: <a className="deb-primo" onClick={() => onApri(primo.id)}>{primo.titolo}</a>,{' '}
          <span className={primo.urgente ? 'is-alert' : undefined}>{primo.stato.charAt(0).toLowerCase() + primo.stato.slice(1)}</span>.</>
      )}
    </>
  );
}

function Colonna({ col, oggi, onApri }: { col: ColonnaBacheca; oggi: string; onApri: (id: string) => void }) {
  return (
    <section className={`kanban-col deb-col deb-col--${col.key}`} aria-label={col.label}>
      {/* 26/10/2026: titolo e totale uno sopra l'altro (affiancati sbordavano a 1280); la
          descrizione della colonna resta come suggerimento sul titolo. */}
      <div className="kanban-col-hd deb-col-hd" title={col.descrizione}>
        <div className="min-w-0">
          <div className="kanban-col-title">
            <span className="dot" />
            {col.label}
            <span className="kanban-col-count">{col.carte.length}</span>
          </div>
          <div className="deb-col-tot">
            <span className="mono">€ {fmtEur(col.totale, true)}</span>
            <span className="deb-col-tot-k">{col.totaleLabel}</span>
          </div>
        </div>
      </div>
      <div className="kanban-col-body">
        {col.carte.length === 0
          ? <div className="deb-vuoto">{col.key === 'sistemare' ? 'Nessun debito da sistemare.' : col.key === 'rate' ? 'Nessun piano a rate in corso.' : 'Ancora nessun debito chiuso.'}</div>
          : col.carte.map((c) => <Carta key={c.id} c={c} oggi={oggi} onApri={onApri} />)}
      </div>
    </section>
  );
}

function Carta({ c, oggi, onApri }: { c: CartaDebito; oggi: string; onApri: (id: string) => void }) {
  return (
    <button type="button" className={`kanban-card deb-card${c.urgente ? ' is-urgente' : ''}`} onClick={() => onApri(c.id)}>
      <div className="deb-doc">{etichettaDoc(c)}</div>
      <div className="kanban-card-title">{c.titolo}</div>
      {c.rate ? (
        <Avanzamento r={c.rate} oggi={oggi} compatto />
      ) : (
        <div className="deb-importo mono">€ {fmtEur(c.importo, true)}</div>
      )}
      <div className={`deb-stato${c.urgente ? ' is-urgente' : ''}`}>{c.stato}</div>
      {c.rate?.prossima?.nota && <div className="deb-avviso">{c.rate.prossima.nota}</div>}
    </button>
  );
}

/** L'etichetta piccola sopra il titolo: il documento, o com'è finita una tassa senza documento. */
function etichettaDoc(c: CartaDebito): string {
  if (c.documento) return DOCUMENTO_LABEL[c.documento];
  return c.colonna === 'chiusi' ? 'Pagata in ritardo' : 'Tassa non versata';
}

/**
 * Barra delle rate: pagate su totali, pagati e restano, prossima rata.
 * `senzaProssima`: nella scheda la prossima rata è la prima riga dell'elenco qui sotto, non si ripete.
 */
function Avanzamento({ r, oggi, compatto, senzaProssima }: { r: NonNullable<CartaDebito['rate']>; oggi: string; compatto?: boolean; senzaProssima?: boolean }) {
  const pct = r.totali ? Math.round((r.pagate / r.totali) * 100) : 0;
  return (
    <div className="deb-av">
      <div className="deb-bar" role="progressbar" aria-valuemin={0} aria-valuemax={r.totali} aria-valuenow={r.pagate}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <div className="deb-av-riga">
        <span>pagati <b className="mono">€ {fmtEur(r.pagato, true)}</b></span>
        <span>restano <b className="mono">€ {fmtEur(r.resta, true)}</b></span>
      </div>
      {r.prossima && !senzaProssima && (
        <div className="deb-av-prossima">
          {compatto ? 'Prossima' : 'Prossima rata'}: {dataCorta(r.prossima.scadenza, oggi)} · <span className="mono">€ {fmtEur(r.prossima.importo, true)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * La scheda del debito, a destra. Esc o clic fuori la chiudono.
 * Sta in un portale su <body>: la pagina ha un'animazione d'entrata con
 * transform, e dentro un antenato trasformato `position: fixed` si aggancia a
 * lui invece che allo schermo (bug visto da Lorenzo il 16/10: cliccando una
 * carta in basso la scheda restava in cima alla pagina).
 */
function Pannello(props: { carta: CartaDebito | null; oggi: string; onClose: () => void }) {
  return createPortal(<PannelloDentro {...props} />, document.body);
}

function PannelloDentro({ carta, oggi, onClose }: { carta: CartaDebito | null; oggi: string; onClose: () => void }) {
  const [vista, setVista] = useState<CartaDebito | null>(carta);
  useEffect(() => { if (carta) setVista(carta); }, [carta]);
  useEffect(() => {
    if (!carta) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [carta, onClose]);
  const c = vista;
  const open = !!carta;

  return (
    <>
      <div className={`deb-velo${open ? ' is-open' : ''}`} onClick={onClose} aria-hidden />
      <aside className={`deb-panel${open ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-label={c?.titolo ?? 'Scheda del debito'}>
        {c && (
          <>
            <div className="deb-panel-hd">
              <div className="min-w-0">
                <div className="deb-doc">{etichettaDoc(c)}</div>
                <h2 className="deb-panel-title">{c.titolo}</h2>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={onClose} title="Chiudi la scheda (Esc)"><Icon name="x" size={14} /></button>
            </div>
            <div className="deb-panel-body">
              <Scheda c={c} oggi={oggi} />
            </div>
          </>
        )}
      </aside>
    </>
  );
}

/**
 * Cos'è il documento, in parole semplici (26/10/2026: "cartella, avviso, F24, 60 giorni finiti,
 * sanzione ridotta" erano gergo per chi non fa l'amministrativo). Una riga sola, nella scheda.
 */
const COS_E: Record<string, string> = {
  CARTELLA: "La cartella è la richiesta di pagamento dell'Agenzia delle Entrate Riscossione: dalla notifica ci sono 60 giorni per pagarla o chiedere le rate, poi partono interessi e azioni di recupero.",
  AVVISO: "L'avviso è la lettera dell'Agenzia delle Entrate su una tassa non pagata o sbagliata: pagando entro la scadenza la sanzione è ridotta.",
  PIANO: 'Il piano a rate è un debito già diviso in rate mensili: basta pagarle una dopo l\'altra. Se se ne saltano troppe, il piano decade.',
  F24: "Un F24 è il modulo con cui si pagano le tasse: questo non è partito. Si può ancora pagare, con una piccola sanzione in più (il ravvedimento).",
};

/** Il contenuto della scheda, uguale su computer e telefono. */
function Scheda({ c, oggi }: { c: CartaDebito; oggi: string }) {
  const chiuso = c.colonna === 'chiusi';
  return (
    <>
      {/* In un debito chiuso resta solo il menu ⋯: sta sulla riga dello stato, non su una riga sua. */}
      <div className="deb-stato-riga">
        <div className={`deb-scheda-stato${c.urgente ? ' is-urgente' : ''}`}>{c.stato}</div>
        {chiuso && <AzioniTassa tassa={c.tassa} rata={c.rataDaPagare} nome={c.titolo} aperta={false} />}
      </div>
      {c.cosaFare && <p className="deb-cosa">{c.cosaFare}</p>}
      {!chiuso && <AzioniTassa tassa={c.tassa} rata={c.rataDaPagare} nome={c.titolo} aperta />}
      {!chiuso && c.documento && COS_E[c.documento] && <p className="deb-cose"><Icon name="help" size={12} /> {COS_E[c.documento]}</p>}

      <dl className="deb-fatti">
        <dt>{c.rate ? 'Totale del piano' : 'Importo'}</dt>
        <dd className="mono">€ {fmtEur(c.importo, true)}</dd>
        {c.numero && (<><dt>Numero</dt><dd className="mono">{c.numero}</dd></>)}
        {c.notificataIl && (<><dt>{c.documento === 'PIANO' ? 'Piano concesso il' : 'Arrivata via PEC'}</dt><dd>{dataCorta(c.notificataIl, oggi)}</dd></>)}
        {!c.rate && c.scadenza && c.documento !== 'F24' && (
          <><dt>{c.documento === 'CARTELLA' || c.documento === 'AVVISO' ? 'Ultimo giorno per pagare' : 'Scadenza'}</dt><dd>{dataCorta(c.scadenza, oggi)}</dd></>
        )}
        {/* La scadenza dell'F24 non si ripete: la dice già lo stato qui sopra ("scaduto il 20/8"). */}
      </dl>

      {c.nota && <p className="deb-nota">{c.nota}</p>}

      {c.dentro.length > 0 && (
        <section className="deb-sez">
          <h3 className="deb-sez-t">Cosa c'è dentro il piano</h3>
          {c.dentro.map((d) => (
            <div key={d.cosa} className="deb-riga">
              <span className="min-w-0 flex-1">{d.cosa}</span>
              <span className="mono">€ {fmtEur(d.importo, true)}</span>
            </div>
          ))}
        </section>
      )}

      {c.rate && (
        <>
          <section className="deb-sez">
            <h3 className="deb-sez-t">Avanzamento del piano</h3>
            <Avanzamento r={c.rate} oggi={oggi} senzaProssima />
          </section>
          <ElencoRate rate={c.rate.elenco} oggi={oggi} />
        </>
      )}
    </>
  );
}

function ElencoRate({ rate, oggi }: { rate: RataDebito[]; oggi: string }) {
  const pagate = rate.filter((r) => r.pagataIl);
  const daPagare = rate.filter((r) => !r.pagataIl);
  return (
    <>
      {daPagare.length > 0 && (
        <section className="deb-sez">
          <h3 className="deb-sez-t">Rate da pagare <span className="deb-sez-n">{daPagare.length}</span></h3>
          {daPagare.map((r, i) => (
            <div key={r.seq} className={`deb-rata${i === 0 ? ' is-prossima' : ''}${r.scadenza < oggi ? ' is-scaduta' : ''}`}>
              <span className="deb-rata-ico" aria-hidden />
              <span className="deb-rata-n">Rata {r.seq}</span>
              <span className="deb-rata-d">{r.scadenza < oggi ? 'scaduta ' : 'entro '}{conArticolo(r.scadenza, oggi)}</span>
              <span className="mono deb-rata-e">€ {fmtEur(r.importo, true)}</span>
              {i === 0 && r.nota && <span className="deb-rata-nota">{r.nota}</span>}
            </div>
          ))}
        </section>
      )}
      {pagate.length > 0 && (
        <section className="deb-sez">
          <h3 className="deb-sez-t">Rate già pagate <span className="deb-sez-n">{pagate.length}</span></h3>
          {[...pagate].reverse().map((r) => (
            <div key={r.seq} className="deb-rata is-pagata">
              <span className="deb-rata-ico" aria-hidden><Icon name="check" size={10} /></span>
              <span className="deb-rata-n">Rata {r.seq}</span>
              <span className="deb-rata-d">pagata {conArticolo(r.pagataIl, oggi)}</span>
              <span className="mono deb-rata-e">€ {fmtEur(r.importo, true)}</span>
            </div>
          ))}
        </section>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Telefono: una colonna alla volta, la scheda nel foglio dal basso
// ════════════════════════════════════════════════════════════════════

const CORTE: Record<ColonnaDebiti, string> = { sistemare: 'Da sistemare', rate: 'A rate', chiusi: 'Chiusi' };

function DebitiMobile({ q, oggi, scelta, onApri }: VistaProps) {
  const [col, setCol] = useUrlState<string>('colonna', 'sistemare');
  const colonne = q.data?.colonne ?? [];
  const attiva = colonne.find((c) => c.key === col) ?? colonne[0];

  // 26/10/2026: dentro `.page` come le altre pagine del telefono (prima il contenuto toccava il
  // bordo dello schermo) e con l'intestazione per prima, così titolo e breadcrumb non ripetono
  // la barra in alto (mobile.md 4bis).
  return (
    <div className="page adm-page">
    <AdminHeader title="Debiti col fisco" />
    <MPage>
      {q.isLoading && !q.data ? (
        <Skeleton size="block" h={180} />
      ) : !attiva ? (
        <MEmpty>Non riesco a leggere i debiti in questo momento. Riprova tra poco.</MEmpty>
      ) : (
        <>
          <MSeg
            value={attiva.key}
            options={colonne.map((c) => ({ key: c.key, label: CORTE[c.key], count: c.carte.length }))}
            onChange={(k) => setCol(k)}
          />
          <div className="deb-m-tot">
            <span className="mono">€ {fmtEur(attiva.totale, true)}</span> {attiva.totaleLabel}
          </div>
          <MGroup title={attiva.label} note={attiva.descrizione}>
            {attiva.carte.length === 0 ? (
              <MEmpty>{attiva.key === 'sistemare' ? 'Nessun debito da sistemare.' : attiva.key === 'rate' ? 'Nessun piano a rate in corso.' : 'Ancora nessun debito chiuso.'}</MEmpty>
            ) : attiva.carte.map((c) => (
              <MRow
                key={c.id}
                title={c.titolo}
                sub={c.stato}
                subWrap
                trail={`€ ${fmtEur(c.rate ? c.rate.resta : c.importo, true)}`}
                trailSub={c.rate ? `${c.rate.pagate} su ${c.rate.totali}` : c.documento ? DOCUMENTO_LABEL[c.documento] : undefined}
                trailTone={c.urgente ? 'alert' : undefined}
                onClick={() => onApri(c.id)}
                chevron
              />
            ))}
          </MGroup>
        </>
      )}

      <Foglio open={!!scelta} onClose={() => onApri('')} title={scelta?.titolo}>
        {scelta && (
          <>
            <Fatto label="Che documento è" value={etichettaDoc(scelta)} />
            <Testo><Scheda c={scelta} oggi={oggi} /></Testo>
          </>
        )}
      </Foglio>
    </MPage>
    </div>
  );
}
